import React, { PropsWithChildren } from "react";

import {
    BlockMap,
    CompositeDecorator,
    EntityDecorators,
    StyleMap,
} from "./types";

// Component implementations

const CodeBlock = ({ children }: PropsWithChildren) => (
    <pre>
        <code>{children}</code>
    </pre>
);

const RenderChildren = ({ children }: PropsWithChildren) => children;

const LinkComponent = ({
    children,
    data: { url },
}: PropsWithChildren<{
    data: {
        url?: string;
    };
}>) => <a href={url}>{children}</a>;

const ImageComponent = ({
    data: { src, alt, width, height },
}: {
    data: {
        src: string;
        alt?: string;
        width?: number;
        height?: number;
    };
}) => <img src={src} alt={alt} width={width} height={height} />;

const EmbedComponent = ({
    data: { html },
    children,
}: PropsWithChildren<{
    data: {
        html?: string;
    };
}>) => {
    if (!html) return children;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

function HorizontalRuleComponent() {
    return <hr />;
}

/**
 * Default block map - matches draftjs_exporter defaults
 */
export const BLOCK_MAP = {
    "unstyled": "p",
    "header-one": "h1",
    "header-two": "h2",
    "header-three": "h3",
    "header-four": "h4",
    "header-five": "h5",
    "header-six": "h6",
    "unordered-list-item": {
        element: "li",
        wrapper: "ul",
        wrapperProps: { className: "bullet-list" },
    },
    "ordered-list-item": {
        element: "li",
        wrapper: "ol",
    },
    "blockquote": "blockquote",
    "pre": "pre",
    "code-block": CodeBlock,
    "atomic": RenderChildren,
    "fallback": "p",
} satisfies BlockMap;
export type DefaultBlockTypes = keyof typeof BLOCK_MAP;

/**
 * Default style map - matches draftjs_exporter defaults
 */
export const STYLE_MAP = {
    BOLD: "strong",
    CODE: "code",
    ITALIC: "em",
    UNDERLINE: "u",
    STRIKETHROUGH: "s",
    SUPERSCRIPT: "sup",
    SUBSCRIPT: "sub",
    MARK: "mark",
    QUOTATION: "q",
    SMALL: "small",
    SAMPLE: "samp",
    INSERT: "ins",
    DELETE: "del",
    KEYBOARD: "kbd",
    FALLBACK: "span",
} satisfies StyleMap;
export type DefaultStyleTypes = keyof typeof STYLE_MAP;

const noop = () => null;
/**
 * Default entity decorators
 */

export const ENTITY_DECORATORS = {
    LINK: LinkComponent,
    IMAGE: ImageComponent,
    EMBED: EmbedComponent,
    HORIZONTAL_RULE: HorizontalRuleComponent,
    DOCUMENT: noop,
    FALLBACK: noop,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
} satisfies EntityDecorators<any>;
export type DefaultEntityTypes = keyof typeof ENTITY_DECORATORS;

/**
 * Default composite decorators - just newline handling
 */
export const COMPOSITE_DECORATORS: CompositeDecorator[] = [
    {
        strategy: /\n/g,
        component: ({ block, children }) => {
            // Don't convert newlines to <br/> in code blocks - preserve actual newlines
            const blockType = "type" in block ? block.type : "unstyled";
            if (blockType === "code-block" || blockType === "pre") {
                return <>{children}</>;
            }
            return <br />;
        },
    },
];
